<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    <FlashMessage v-if="messages.text" :message="messages" />
    <!-- Download Success Notification with link -->
    <transition name="fade">
      <div
        v-if="showDownloadSuccess"
        class="fixed top-5 right-5 z-50 flex items-center gap-3 bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-5 h-5 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          />
        </svg>
        <span class="text-sm font-medium">Report downloaded successfully!</span>
        <button
          @click="goToDownloadLogs"
          class="underline text-sm font-semibold whitespace-nowrap hover:text-green-200 transition"
        >
          Click to view
        </button>
        <button
          @click="showDownloadSuccess = false"
          class="ml-1 hover:text-green-200 transition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </transition>
    <div
      class="container mx-auto max-w-[1200px] w-full py-7 flex flex-col gap-10 px-4 md:px-6 lg:gap-16"
    >
      <!-- Step Indicator -->
      <div class="flex items-center justify-center gap-4 md:gap-8">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="flex items-center gap-2 md:gap-4"
        >
          <div
            class="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full font-semibold text-sm md:text-base transition-all duration-200"
            :class="{
              'bg-blue-600 text-white shadow-md': currentStep >= index + 1,
              'bg-gray-200 text-gray-500': currentStep < index + 1,
            }"
          >
            {{ index + 1 }}
          </div>
          <span
            class="text-sm md:text-base font-medium hidden sm:block transition-colors duration-200"
            :class="{
              'text-blue-600': currentStep >= index + 1,
              'text-gray-400': currentStep < index + 1,
            }"
          >
            {{ step }}
          </span>
          <div
            v-if="index < steps.length - 1"
            class="w-8 md:w-12 h-[2px] rounded-full"
            :class="{
              'bg-blue-600': currentStep > index + 1,
              'bg-gray-200': currentStep <= index + 1,
            }"
          ></div>
        </div>
      </div>

      <!-- Step 1: Campaign Details -->
      <div
        v-if="currentStep === 1"
        class="bg-white rounded-2xl shadow-lg p-6 md:p-8 transition-all duration-300"
      >
        <div class="flex items-center gap-3 mb-6">
          <div class="w-1 h-8 bg-blue-600 rounded-full"></div>
          <h2 class="text-2xl font-bold text-gray-800">Configure Report</h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Report Title
            </label>
            <input
              v-model="campaignData.templateTitle"
              type="text"
              required
              placeholder="e.g., Course progress report "
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200"
            />
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <input
              v-model="campaignData.subject"
              type="text"
              required
              placeholder="e.g., Subject Wise Attandance"
              class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200"
            />
          </div>
        </div>

        <div class="flex justify-end items-center gap-3 pb-4 mt-3">
          <button
            type="submit"
            @click="handleSave"
            :disabled="
              disable_submit == true ||
              !campaignData.templateTitle ||
              !campaignData.subject
            "
            :class="{
              'bg-gray-200 text-black cursor-not-allowed':
                disable_submit == true ||
                !campaignData.templateTitle ||
                !campaignData.subject,
              'bg-blue-600 hover:bg-[#18469d] cursor-pointer text-white': !(
                disable_submit == true ||
                !campaignData.templateTitle ||
                !campaignData.subject
              ),
            }"
            class="rounded-lg text-sm px-6 py-2"
          >
            Continue
            <span class="ml-2">→</span>
          </button>

          <img
            v-if="disable_submit"
            src="@/assets/loading.gif"
            alt="loading"
            class="w-6 h-6 object-contain"
          />
        </div>
      </div>

      <!-- Step 2: Course Selection -->
      <div
        v-if="currentStep === 2"
        class="bg-white rounded-2xl shadow-lg p-6 md:p-8 transition-all duration-300"
      >
        <div class="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div class="flex items-center gap-3">
            <div class="w-1 h-8 bg-blue-600 rounded-full"></div>
            <h2 class="text-2xl font-bold text-gray-800">
              Select Courses
              <span class="ml-2 text-sm font-normal text-gray-500">
                ({{ totalCoursesCount || 0 }})
              </span>
            </h2>
          </div>

          <span
            v-if="selectedCourseIds.length > 0"
            class="text-sm font-medium text-indigo-600"
          >
            Selected: {{ selectedCourseIds.length }}
          </span>
        </div>

        <!-- Search and Select All Bar -->
        <div class="flex flex-col gap-4 bg-gray-50 rounded-xl p-4 mb-6">
          <div class="flex flex-col sm:flex-row justify-between gap-4">
            <div class="flex justify-end">
              <div class="relative w-full max-w-80">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  v-model="searchQuery"
                  @input="getCourses(1)"
                  placeholder="Search..."
                  class="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <button
                  v-if="searchQuery"
                  @click="clearSearch"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-gray-400 hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div class="flex gap-3">
              <label
                class="flex items-center gap-2 cursor-pointer px-4 py-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition"
              >
                <input
                  type="checkbox"
                  v-model="courseSelectAll"
                  @change="toggleCourseSelectAll"
                  class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span class="font-medium text-gray-700"
                  >Select All on Page</span
                >
              </label>
            </div>
          </div>

          <div>
            <div class="grid grid-cols-1 md:grid-cols-6 gap-5">
              <select
                id="batch_id"
                v-model="savedFilters.batch_id"
                @change="onBatchChange"
                class="bg-white rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold capitalize leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-100"
              >
                <option value="" disabled>Select Batch</option>
                <option
                  v-for="batch_year in batch_years"
                  :key="batch_year.id"
                  :value="batch_year.id"
                >
                  {{ batch_year.year }}
                </option>
              </select>

              <!-- Program Name -->

              <select
                id="program_id"
                v-model="savedFilters.program_id"
                @change="onProgramChange"
                required
                class="bg-white rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold capitalize leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-100"
              >
                <option value="" disabled selected>Select Program</option>
                <option
                  v-for="program in programs"
                  :key="program.id"
                  :value="program.id"
                >
                  {{ program.name }}
                </option>
              </select>
              <!-- department -->
              <!-- <select
                id="department_id"
                v-model="savedFilters.department_id"
                @change="onDepartmentChange"
                required
                class="bg-white rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold capitalize leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-100"
              >
                <option value="" disabled selected>Select Department</option>
                <option
                  v-for="department in departments"
                  :key="department.id"
                  :value="department.id"
                >
                  {{ department.name }}
                </option>
              </select> -->

              <!-- Semester -->

              <select
                id="semester_id"
                v-model="savedFilters.semester_id"
                @change="onSemesterChange"
                required
                class="bg-white rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold capitalize leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-100"
              >
                <option value="" disabled selected>Select Semester</option>
                <option
                  v-for="semester in semetser_list"
                  :key="semester.id"
                  :value="semester.id"
                >
                  {{ semester.semester }}
                </option>
              </select>

              <select
                id="selected_publish"
                v-model="savedFilters.publish_id"
                @change="onPublishChange"
                class="bg-white rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold capitalize leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-100"
              >
                <option value="" disabled selected>Select Status</option>
                <option value="true">Published</option>
                <option value="false">Unpublished</option>
              </select>

              <button
                type="button"
                @click="clearFilters"
                class="text-blue-600 underline flex items-center text-xs font-semibold gap-1 hover:text-blues"
              >
                <img
                  src="@/assets/attendance/cancel.webp"
                  alt="Clear"
                  class="w-3 h-3 object-contain"
                />
                Clear All
              </button>
            </div>
          </div>
        </div>

        <!-- Course Cards Grid -->
        <div v-if="courselist.length > 0">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="course in courselist"
              :key="course.id"
              class="group border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 bg-white hover:-translate-y-1"
              :class="{
                'ring-2 ring-blue-500 shadow-lg': selectedCourseIds.includes(
                  course.id,
                ),
              }"
            >
              <div
                class="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200"
              >
                <img
                  :src="course.thumbnail"
                  :alt="course.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div class="absolute top-3 right-3">
                  <label class="cursor-pointer">
                    <input
                      type="checkbox"
                      :value="course.id"
                      v-model="selectedCourseIds"
                      class="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 bg-white shadow-md cursor-pointer"
                    />
                  </label>
                </div>
                <div
                  class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3"
                >
                  <span
                    class="text-xs font-medium text-white bg-blue-600/80 px-2 py-0.5 rounded-full"
                  >
                    {{ course.student_count || 0 }} students
                  </span>
                </div>
              </div>
              <div class="p-4">
                <h3
                  class="font-semibold text-gray-800 line-clamp-2 group-hover:text-blue-600 transition"
                >
                  {{ course.title }}
                </h3>
                <p class="text-xs text-gray-500 mt-1">
                  Batch: {{ course?.batch?.year || "---" }}
                </p>
                <p class="text-xs text-gray-500 mt-1">
                  Program: {{ course?.program?.name || "---" }}
                </p>
                <!-- <p class="text-xs text-gray-500 mt-1">
                  Department: {{ course?.department?.name || "---" }}
                </p> -->
                <p class="text-xs text-gray-500 mt-1">
                  Semester: {{ course?.semester?.semester || "---" }}
                </p>
                <p class="text-xs text-gray-500 mt-1">
                  Publish:
                  {{
                    course?.is_published === true
                      ? "Published"
                      : course?.is_published === false
                      ? "Unpublished"
                      : "---"
                  }}
                </p>
              </div>
            </div>
          </div>
          <Pagination
            :totalPages="totalPages"
            :currentPage="current_page"
            ref="paginationRef"
            @page-changed="handlePageChange"
          />
        </div>
        <!-- Empty State -->
        <div v-else class="text-center py-16">
          <svg
            class="w-16 h-16 mx-auto text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p class="text-gray-500 text-lg">
            No courses found matching your search.
          </p>
          <button
            @click="clearFilters"
            class="mt-2 text-blue-600 hover:underline"
          >
            Clear search
          </button>
        </div>

        <div class="flex justify-between mt-10">
          <button
            @click="prevStep"
            class="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
          >
            ← Back
          </button>
          <div class="flex gap-3">
            <button
              @click="nextStep"
              :disabled="disable_submit || selectedCourseIds.length === 0"
              :class="{
                'bg-gray-200 text-black cursor-not-allowed':
                  disable_submit || selectedCourseIds.length === 0,
                'bg-blue-600 hover:bg-[#18469d] cursor-pointer text-white': !(
                  disable_submit || selectedCourseIds.length === 0
                ),
              }"
              class="rounded-lg text-sm px-6 py-2"
            >
              Continue →
            </button>
            <img
              v-if="disable_submit"
              src="@/assets/loading.gif"
              alt="loading"
              class="w-6 h-6 object-contain"
            />
          </div>
        </div>
      </div>

      <!-- Step 3: Student Report -->
      <div
        v-if="currentStep === 3"
        class="bg-white rounded-2xl shadow-lg p-6 md:p-8 transition-all duration-300"
      >
        <div>
          <div class="flex items-center gap-3 mb-8">
            <div class="w-1 h-8 bg-blue-600 rounded-full"></div>
            <h2 class="text-2xl font-bold text-gray-800">
              Select Data Sources
            </h2>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- Card 1: Download -->
            <div
              @click="DownloadReport"
              class="group cursor-pointer border-2 border-gray-200 hover:border-blue-500 rounded-2xl p-8 flex flex-col items-center justify-center gap-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white"
            >
              <div
                class="w-16 h-16 bg-blue-50 group-hover:bg-blue-100 rounded-full flex items-center justify-center transition-all duration-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-8 h-8 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </div>
              <div class="text-center">
                <h3
                  class="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-200"
                >
                  Course Level Information
                </h3>
                <p class="text-sm text-gray-500 mt-1">
                  Download the course details report
                </p>
                <div class="flex justify-center mt-1">
                  <ul class="list-disc text-sm text-left text-gray-500">
                    <li>Course Summary</li>
                    <li>Module Completion</li>
                    <li>Assignment Detail</li>
                    <li>Assessment Detail</li>
                    <li>Video Content</li>
                    <li>Engagement Scores</li>
                    <li>Live Class Status</li>
                  </ul>
                </div>
              </div>
              <span
                class="mt-2 inline-flex items-center gap-2 px-5 py-2 bg-blue-600 group-hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all duration-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download
              </span>
            </div>

            <!-- Card 2: Next Step -->
            <div
              @click="goToStudentTable"
              class="group cursor-pointer border-2 border-gray-200 hover:border-green-500 rounded-2xl p-8 flex flex-col items-center justify-center gap-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white"
            >
              <div
                class="w-16 h-16 bg-green-50 group-hover:bg-green-100 rounded-full flex items-center justify-center transition-all duration-300"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-8 h-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div class="text-center">
                <h3
                  class="text-lg font-bold text-gray-800 group-hover:text-green-600 transition-colors duration-200"
                >
                  Student Level Information
                </h3>
                <p class="text-sm text-gray-500 mt-1">
                  View and manage student report details
                </p>
                <div class="flex justify-center mt-1">
                  <ul class="list-disc text-sm text-left text-gray-500">
                    <li>Course & Student Information</li>
                    <li>Module Completion</li>
                    <li>Login & Activity Details</li>
                    <li>Course Progress Overview</li>
                    <li>Lesson Progress</li>
                    <li>Assignment Performance</li>
                    <li>Assessment Detail</li>
                    <li>Video Engagement</li>
                  </ul>
                </div>
              </div>
              <span
                class="mt-2 inline-flex items-center gap-2 px-5 py-2 bg-green-600 group-hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-all duration-200"
              >
                Continue
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div class="flex justify-between mt-10">
            <button
              @click="prevStep"
              class="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>
      <!-- Step 4: Student List -->
      <div
        v-if="currentStep === 4"
        class="bg-white rounded-2xl shadow-lg p-6 md:p-8 transition-all duration-300"
      >
        <div class="flex items-center justify-between flex-wrap gap-4 mb-2">
          <div class="flex items-center gap-3">
            <div class="w-1 h-8 bg-blue-600 rounded-full"></div>
            <h2 class="text-2xl font-bold text-gray-800">Student List</h2>
          </div>
          <span
            v-if="userSelectList.length > 0"
            class="text-sm font-medium text-indigo-600"
          >
            Selected: {{ userSelectList.length }}
          </span>
        </div>
        <p class="text-gray-500 text-sm mb-6 pl-4">
          Students enrolled in selected courses
        </p>

        <!-- Summary Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div
            class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center"
          >
            <p class="text-3xl font-bold text-blue-600">
              {{ selectedCourseIds.length }}
            </p>
            <p class="text-sm text-gray-600">Courses Selected</p>
          </div>
          <!-- <div
            class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center"
          >
            <p class="text-3xl font-bold text-green-600">
              {{ studentList.length }}
            </p>
            <p class="text-sm text-gray-600">Unique Students</p>
          </div> -->
          <div
            class="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center"
          >
            <p class="text-3xl font-bold text-purple-600">
              {{ studentCount }}
            </p>
            <p class="text-sm text-gray-600">Total Enrollments</p>
          </div>
        </div>
        <div class="flex justify-end mb-5 gap-3">
          <div class="relative w-full max-w-80">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              v-model="searchStudentQuery"
              @input="fetchstudent(1)"
              placeholder="Search..."
              class="w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <button
              v-if="searchStudentQuery"
              @click="(this.searchStudentQuery = ''), this.fetchstudent(1)"
              class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-gray-400 hover:text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <!-- Start Date -->
          <div class="flex flex-col relative">
            <label
              class="absolute -top-2.5 left-3 bg-gray-50 px-1 text-xs font-medium text-gray-600"
            >
              Start Date
            </label>
            <input
              type="date"
              v-model="startDate"
              class="px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
            />
          </div>

          <!-- End Date -->
          <div class="flex flex-col relative">
            <label
              class="absolute -top-2.5 left-3 bg-gray-50 px-1 text-xs font-medium text-gray-600"
            >
              End Date
            </label>
            <input
              type="date"
              v-model="endDate"
              class="px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full"
            />
          </div>

          <!-- Apply Button -->
          <button
            @click="applyCustomDate"
            class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
          >
            Apply
          </button>

          <!-- Clear/Close Icon Button (shows when dates are selected) -->
          <button
            v-if="startDate || endDate"
            @click="clearDates"
            class="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-lg transition-all duration-200"
            title="Clear dates"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Student Table -->
        <div class="overflow-x-auto rounded-xl border border-gray-200">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="py-3 px-4 font-semibold text-blues text-center">
                  <input
                    type="checkbox"
                    v-model="selectAll"
                    @change="toggleSelectAll"
                    class="w-4 h-4 rounded text-blue-600 border-gray-400"
                  />
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-blues tracking-wider"
                >
                  S.No
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-blues tracking-wider"
                >
                  Student
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-blues tracking-wider"
                >
                  Email
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-blues tracking-wider"
                >
                  Program
                </th>
                <!-- <th
                  class="px-6 py-4 text-left text-xs font-semibold text-blues tracking-wider"
                >
                  Department
                </th> -->
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(student, index) in studentList"
                :key="student.user_id"
                class="hover:bg-gray-50 transition"
              >
                <td class="py-3 px-4 text-center">
                  <input
                    type="checkbox"
                    :value="student?.user_id"
                    v-model="userSelectList"
                    class="w-4 h-4 rounded text-blue-600 border-gray-400"
                  />
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ (studentCurrentPage - 1) * studentPageSize + index + 1 }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div
                      class="w-8 h-8 rounded-full bg-blue-100 flex items-center capitalize justify-center text-blue-600 font-semibold text-sm"
                    >
                      {{ student.display_name.charAt(0) }}
                    </div>
                    <span
                      class="ml-3 text-sm font-medium text-gray-900 capitalize"
                      >{{ student.display_name }}</span
                    >
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ student?.user_email }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-500">
                  {{ student?.program }}
                </td>
                <!-- <td class="px-6 py-4 text-sm text-gray-500">
                  {{ student?.department }}
                </td> -->
              </tr>
              <tr v-if="studentList.length === 0">
                <td colspan="6" class="px-6 py-12 text-center text-gray-500">
                  No data found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-6" v-if="studentTotalPages > 1">
          <Pagination
            :totalPages="studentTotalPages"
            :currentPage="studentCurrentPage"
            @page-changed="handleStudentPageChange"
          />
        </div>
        <div class="flex justify-between mt-8">
          <button
            @click="prevStep"
            class="px-6 py-2 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200"
          >
            ← Back
          </button>
          <div class="flex gap-3 items-center">
            <button
              @click="submitCampaign"
              :disabled="disable_submit || userSelectList.length === 0"
              :class="{
                'bg-gray-200 text-black cursor-not-allowed':
                  disable_submit || userSelectList.length === 0,
                'bg-blue-600 hover:bg-[#18469d] cursor-pointer text-white': !(
                  disable_submit || userSelectList.length === 0
                ),
              }"
              class="rounded-lg text-sm px-6 py-2"
            >
              Submit
            </button>
            <img
              v-if="disable_submit"
              src="@/assets/loading.gif"
              alt="loading"
              class="w-6 h-6 object-contain"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import { mapGetters } from "vuex";
import axios from "axios";
import Pagination from "@/components/Pagination.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import router from "@/router";

export default {
  name: "CreateCampaign",
  components: { Header, FlashMessage, Pagination },

  data() {
    return {
      currentStep: 1,
      steps: ["Configure Report", "Select Courses", "Export"],
      campaignData: {
        templateTitle: "",
        subject: "",
      },
      searchQuery: "",
      showSuccessModal: false,
      submitted: false,
      messages: {
        text: "",
        type: "null",
      },
      courselist: [],
      totalPages: 1,
      current_page: 1,
      pageSize: 10,
      totalCoursesCount: 0,
      allCoursesSelectedFlag: false,

      courseSelectAll: false,
      courseAllList: [],
      selectedCourseIds: [],
      disable_submit: false,

      studentList: [],
      studentAllList: [],
      studentTotalPages: 1,
      studentCurrentPage: 1,
      studentPageSize: 10,
      studentCount: 0,
      selectAll: false,
      userSelectList: [],
      response_message: "",
      searching: "",
      campaignId: "",
      programs: [],
      departments: [],
      semetser_list: [],
      batch_years: [],
      searchStudentQuery: "",
      savedFilters: {
        batch_id: "",
        program_id: "",
        department_id: "",
        semester_id: "",
        publish_id: "",
      },
      startDate: "",
      endDate: "",
      showDownloadSuccess: false,
    };
  },

  computed: {
    ...mapGetters(["userInfo"]),
    totalEnrollments() {
      return this.studentList.length || 0;
    },
  },
  watch: {
    selectedCourseIds(newVal) {
      if (!this.courseAllList.length) return;

      const validIds = this.courseAllList.map((x) => x.id);

      const filtered = newVal.filter((id) => validIds.includes(id));

      if (filtered.length !== newVal.length) {
        this.selectedCourseIds = filtered;
        return;
      }

      this.courseSelectAll =
        filtered.length > 0 && filtered.length === this.courseAllList.length;
    },
    userSelectList(newVal) {
      if (!this.studentAllList.length) return;

      const validIds = this.studentAllList.map((x) => x.user_id);

      const filtered = newVal.filter((id) => validIds.includes(id));

      if (filtered.length !== newVal.length) {
        this.userSelectList = filtered;
        return;
      }

      this.selectAll =
        filtered.length > 0 && filtered.length === this.studentAllList.length;
    },
  },

  mounted() {
    // this.resetCampaign();
  },
  async created() {
    await this.getbatch();
  },

  methods: {
    async getbatch() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/batch-list/?Key=${this.userInfo.token_key}`,
        );
        this.batch_years = res.data.data || [];
      } catch (err) {
        console.error("Error fetching batches:", err);
        this.batch_years = [];
      }
    },
    async onBatchChange() {
      const batchId = this.savedFilters.batch_id;

      // Reset dependent filters only
      this.savedFilters.program_id = "";
      this.savedFilters.department_id = "";
      this.savedFilters.semester_id = "";
      this.savedFilters.publish_id = "";

      this.programs = [];
      this.departments = [];
      this.semetser_list = [];

      if (batchId) {
        await this.getProgramsByBatch(batchId);
      }

      this.fetchcourses();
      this.getCourses(1);
    },

    goToStudentTable() {
      this.currentStep = 4;
      this.fetchAllstudent();
      this.fetchstudent(1);
    },
    applyCustomDate() {
      this.studentCurrentPage = 1;
      this.fetchstudent(1);
    },
    clearDates() {
      this.startDate = "";
      this.endDate = "";
      this.studentCurrentPage = 1;
      this.fetchstudent(1);
    },
    async getProgramsByBatch(batchId) {
      if (!batchId) {
        this.programs = [];
        return;
      }

      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API}/program-list/?batch__id=${batchId}&Key=${this.userInfo.token_key}`,
        );
        this.programs = res.data.data || [];
      } catch (err) {
        console.error("Error fetching programs:", err);
        this.programs = [];
      }
    },
    async onProgramChange() {
      this.savedFilters.department_id = "";
      this.savedFilters.semester_id = "";

      this.departments = [];
      this.semetser_list = [];

      await this.getProgramsFilter();

      this.fetchcourses();
      this.getCourses(1);
    },

    onDepartmentChange() {
      this.getCourses(1);
      this.getSemesterFilter();
      this.fetchcourses();
    },

    onSemesterChange() {
      this.getCourses(1);
      this.fetchcourses();
    },

    onPublishChange() {
      this.getCourses(1);
      this.fetchcourses();
    },
    getProgramsFilter() {
      this.courselist = [];

      let program_id = this.savedFilters.program_id;
      if (!program_id) return;

      axios
        .get(
          `${process.env.VUE_APP_API}/department-list/?Key=${this.userInfo.token_key}&program__id=${program_id}`,
        )
        .then((res) => {
          // this.departments = res.data.data;
          this.departments = res.data.data.map((item) => ({
            id: item.id,
            name: item.name,
          }));

          if (this.departments.length > 0) {
            this.savedFilters.department_id = this.departments[0].id;
            this.getCourses(1);
            this.getSemesterFilter();
            this.fetchcourses();
          }
        });

      axios
        .get(
          `${process.env.VUE_APP_API}/courses/?program_id=${program_id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data.message == "No courses details") {
          } else {
            this.totalPages = response.data.data.total_page;
            this.courselist = response.data.data.data.data;
            this.loading = false;
          }
        });
    },

    getSemesterFilter() {
      this.courselist = [];

      let batch_year = this.savedFilters.batch_id;
      let department_id = this.savedFilters.department_id;
      if (!department_id) return;

      axios
        .get(
          `${process.env.VUE_APP_API}/semetser-get/?Key=${this.userInfo.token_key}&department__id=${department_id}&batch__id=${batch_year}`,
        )
        .then((res) => {
          // this.semetser_list = res.data.data;
          this.semetser_list = res.data.data;
        });

      let program_id = this.savedFilters.program_id;

      axios
        .get(
          `${process.env.VUE_APP_API}/courses/?department_id=${department_id}&program_id=${program_id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data.message == "No courses details") {
          } else {
            this.totalPages = response.data.data.total_page;
            this.courselist = response.data.data.data.data;
            this.loading = false;
          }
        });
    },
    clearFilters() {
      this.savedFilters = {
        batch_id: "",
        program_id: "",
        department_id: "",
        semester_id: "",
        publish_id: "",
      };
      this.programs = [];
      this.departments = [];
      this.semetser_list = [];
      this.selectedCourseIds = [];
      this.searchQuery = "";
      this.fetchcourses();
      this.getCourses(1);
    },
    async handleSave() {
      this.submitted = true;
      this.disable_submit = true;
      const postData = {
        title: this.campaignData.templateTitle,
        subject: this.campaignData.subject,
      };

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/student-data-store/`,
          postData,
          {
            params: {
              Key: this.userInfo?.token_key,
            },
          },
        );

        if (response.data.status === 200) {
          this.campaignId = response.data.data.id;
          this.messages = {
            text: response.data.message,
            type: "success",
          };

          setTimeout(() => {
            this.messages = { text: "", type: "null" };
            this.disable_submit = false;
            this.nextStep();
          }, 1500);
        } else {
          this.messages = {
            text: response.data.message || "An unexpected error occurred.",
            type: "error",
          };

          setTimeout(() => {
            this.disable_submit = false;
            this.messages = { text: "", type: "null" };
          }, 2000);
        }
      } catch (err) {
        console.error("POST Error:", err.response?.data || err.message);

        this.messages = {
          text:
            err.response?.data?.message ||
            "An error occurred while saving the data.",
          type: "error",
        };
      }
    },
    async fetchcourses() {
      let batch_id = this.savedFilters.batch_id || "";
      let department_id = this.savedFilters.department_id || "";
      let program_id = this.savedFilters.program_id || "";
      let semester_id = this.savedFilters.semester_id || "";
      let publish_id = this.savedFilters.publish_id || "";
      await axios
        .get(
          `${process.env.VUE_APP_API}/courses/title/?Key=${this.userInfo.token_key}&search=${this.searchQuery}&batch_id=${batch_id}&dep_id=${department_id}&program_id=${program_id}&semester_id=${semester_id}&is_published=${publish_id}`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.courseAllList = response.data.data || [];
            this.totalCoursesCount = response.data.count;
          }
          // this.selectedCourseIds = [];
          this.courseSelectAll = false;
        });
    },

    async getCourses(page) {
      this.current_page = page;
      let batch_id = this.savedFilters.batch_id || "";
      let department_id = this.savedFilters.department_id || "";
      let program_id = this.savedFilters.program_id || "";
      let semester_id = this.savedFilters.semester_id || "";
      let publish_id = this.savedFilters.publish_id || "";

      let params = {
        Key: this.userInfo?.token_key,
        page: page,
        page_size: this.pageSize,
        batch_id: batch_id,
        dep_id: department_id,
        program_id: program_id,
        semester_id: semester_id,
        is_published: publish_id,
      };

      if (this.searchQuery) {
        params.search = this.searchQuery;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/courses/`,
          { params },
        );

        if (response.data?.data?.data?.status === 200) {
          this.totalPages = response.data.data.total_page;
          this.current_page = response.data.data.current_page;
          this.courselist = response.data.data.data?.data;
          this.totalCoursesCount = response.data.data.total;
        } else if (response.data.status == 400) {
          this.totalCoursesCount = 0;
          this.courselist = [];
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.messages = {
          text: "Failed to load courses. Please try again.",
          type: "error",
        };
      }
    },
    toggleCourseSelectAll() {
      if (this.courseSelectAll) {
        this.selectedCourseIds = this.courseAllList.map((x) => x.id);
      } else {
        this.selectedCourseIds = [];
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.userSelectList = this.studentAllList.map((x) => x.user_id);
      } else {
        this.userSelectList = [];
      }
    },
    async fetchAllstudent() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/students-by-course-without-pagination/?course_id=${this.selectedCourseIds}&Key=${this.userInfo.token_key}&search=${this.searchStudentQuery}&start_date=${this.startDate}&end_date=${this.endDate}&user__demo_user=false`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.studentAllList = response.data.data || [];
            this.studentCount = response.data.count;
          }
          // this.userSelectList = [];
          this.selectAll = false;
        });
    },
    async fetchstudent(page) {
      this.studentCurrentPage = page;

      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.studentPageSize,
        user__demo_user: false,
      };

      if (this.searchStudentQuery) {
        params.search = this.searchStudentQuery;
      }
      if (this.startDate) {
        params.start_date = this.startDate;
      }
      if (this.endDate) {
        params.end_date = this.endDate;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/students-by-course/?course_id=${this.selectedCourseIds}`,
          { params },
        );
        if (response.data.data.status === 200) {
          this.studentList = response.data.data.data;
          this.studentCount = response.data.total;
          this.studentTotalPages = response.data.total_page;
          this.studentCurrentPage = response.data.current_page;
          this.loading = false;
        } else {
          this.studentList = [];
          this.studentCount = 0;
          this.studentTotalPages = 1;
        }
      } catch (error) {
        console.error(error);
      }
    },
    handleStudentPageChange(page) {
      this.fetchstudent(page);
    },
    clearSearch() {
      this.searchQuery = "";
      this.getCourses(1);
    },

    handlePageChange(page) {
      this.getCourses(page);
    },

    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++;
        if (this.currentStep === 2) {
          this.getCourses(this.current_page);
          this.fetchcourses();
        }
        if (this.currentStep === 3) {
          this.fetchAllstudent();
          this.fetchstudent(1);
        }
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        if (this.currentStep === 2) {
          this.getCourses(this.current_page);
          this.fetchcourses();
          if (this.savedFilters.program_id) {
            this.getProgramsFilter();
          }
          if (this.savedFilters.department_id) {
            this.getSemesterFilter();
          }
        }
      }
    },
    async DownloadReport() {
      try {
        this.disable_submit = true;

        const url = `${process.env.VUE_APP_API}/download-history/${this.campaignId}/export/?Key=${this.userInfo.token_key}`;
        const payload = {
          data: "course_details",
          course_ids: this.selectedCourseIds,
        };

        const response = await axios.post(url, payload);

        if (response.data.status === 200) {
          this.messages = {
            text: response.data.message || "Report queued successfully!",
            type: "success",
          };
          setTimeout(() => {
            this.messages = { text: "", type: "null" };
            this.$router.push({ name: "DownloadHistory" });
          }, 1500);
        } else {
          this.messages = {
            text: response.data.message || "Failed to queue report.",
            type: "error",
          };
          setTimeout(() => {
            this.messages = { text: "", type: "null" };
          }, 2000);
        }
      } catch (err) {
        console.error("Download error:", err);
        this.messages = {
          text: err.response?.data?.message || "Failed to queue report.",
          type: "error",
        };
        setTimeout(() => {
          this.messages = { text: "", type: "null" };
        }, 2000);
      } finally {
        this.disable_submit = false;
      }
    },
    goToDownloadLogs() {
      this.showDownloadSuccess = false;
      this.$router.push({ name: "DownloadLogs" });
    },
    async submitCampaign() {
      this.disable_submit = true;
      let urls = `${process.env.VUE_APP_API}/download-history/${this.campaignId}/export/?Key=${this.userInfo.token_key}`;
      const campaignPayload = {
        data: "student_detail",
        student_ids: this.userSelectList,
        course_ids: this.selectedCourseIds,
        start_date: this.startDate,
        end_date: this.endDate,
        report_type: "student_detail",
      };
      await axios
        .post(urls, campaignPayload)
        .then((response) => {
          if (response.data.status == 200) {
            this.messages = {
              text: response.data.message,
              type: "success",
            };

            setTimeout(() => {
              this.messages = {
                text: "",
                type: "null",
              };
              this.$router.push({
                name: "DownloadHistory",
              });
            }, 3000);
          } else {
            this.messages = {
              text: " Event Faild",
              type: "error",
            };
            setTimeout(() => {
              this.messages = {
                text: "",
                type: "null",
              };
            }, 3000);
          }
        })
        .finally(() => {
          setTimeout(() => {
            this.disable_submit = false;
          }, 3000);
        });
    },

    resetCampaign() {
      this.currentStep = 1;
      this.campaignData = { templateTitle: "", subject: "", content: "" };
      this.searchQuery = "";
      this.selectedCourseIds = [];
      this.allCoursesSelectedFlag = false;
      this.showSuccessModal = false;
      this.submitted = false;
      this.current_page = 1;
      this.courselist = [];
      this.programs = [];
      this.departments = [];
      this.semetser_list = [];
      this.savedFilters = {
        program_id: "",
        department_id: "",
        semester_id: "",
        publish_id: "",
      };
    },

    closeModal() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out;
}

.animate-scaleIn {
  animation: scaleIn 0.2s ease-out;
}
</style>
